import { DocumentTextIcon, TagIcon } from "@heroicons/react/outline";
import {
  DocumentTextIcon as DocumentTextIconSolid,
  TagIcon as TagIconSolid,
} from "@heroicons/react/solid";
import { useEffect, useState } from "react";

import ReleaseLabelSelector, {
  ReleaseLabel,
} from "@/components/EvaluationHistory/ControlBar/HistoryFilter/ReleaseLabelSelector";
import PromptVersionSelector from "@/components/ui/PromptVersionSelector";
import TabNavigation from "@/components/ui/TabNavigation";
import { TabItem } from "@/components/ui/TabNavigation/types";
import { ListPromptVersion } from "@/types";

interface GenericDualTabSelectorProps<TFirstType, TSecondType> {
  firstTabName: string;
  secondTabName: string;
  firstTabIcon: {
    outline: React.ReactNode;
    solid: React.ReactNode;
  };
  secondTabIcon: {
    outline: React.ReactNode;
    solid: React.ReactNode;
  };
  firstTabContent: React.ReactNode;
  secondTabContent: React.ReactNode;
  onSelectionChange?: (selection: {
    type: "first" | "second";
    first?: TFirstType | null;
    second?: TSecondType | null;
  }) => void;
  initialSelection?: {
    type: "first" | "second";
    first?: TFirstType | null;
    second?: TSecondType | null;
  };
  showOptionalLabel?: boolean;
}

const GenericDualTabSelector = <TFirstType, TSecondType>({
  firstTabName,
  secondTabName,
  firstTabIcon,
  secondTabIcon,
  firstTabContent,
  secondTabContent,
  onSelectionChange,
  initialSelection,
  showOptionalLabel = true,
}: GenericDualTabSelectorProps<TFirstType, TSecondType>) => {
  const handleTabChange = (index: number) => {
    const newSelectionType = index === 0 ? "first" : "second";

    // Notify parent of tab change
    onSelectionChange?.({
      type: newSelectionType,
      first: newSelectionType === "first" ? initialSelection?.first || null : null,
      second: newSelectionType === "second" ? initialSelection?.second || null : null,
    });
  };

  const tabs: TabItem[] = [
    {
      id: 0,
      name: firstTabName,
      outlineIcon: firstTabIcon.outline,
      solidIcon: firstTabIcon.solid,
      content: firstTabContent,
    },
    {
      id: 1,
      name: secondTabName,
      outlineIcon: secondTabIcon.outline,
      solidIcon: secondTabIcon.solid,
      content: secondTabContent,
    },
  ];

  return (
    <div className="space-y-2">
      {showOptionalLabel && <p className="text-sm font-medium text-gray-500">(Optional)</p>}
      <div className="rounded-lg border border-gray-200 shadow-sm">
        <TabNavigation handleTabChange={handleTabChange} tabs={tabs} />
      </div>
    </div>
  );
};

interface PromptVersionLabelSelectorProps {
  promptId: number;
  onSelectionChange?: (selection: {
    type: "version" | "label";
    version?: ListPromptVersion | null;
    label?: ReleaseLabel | null;
  }) => void;
  initialSelection?: {
    type: "version" | "label";
    version?: ListPromptVersion | null;
    label?: ReleaseLabel | null;
  };
}

const PromptVersionLabelSelector = ({
  promptId,
  onSelectionChange,
  initialSelection,
}: PromptVersionLabelSelectorProps) => {
  const [selectedVersion, setSelectedVersion] = useState<ListPromptVersion | null>(
    initialSelection?.version || null,
  );
  const [selectedLabel, setSelectedLabel] = useState<ReleaseLabel | null>(
    initialSelection?.label || null,
  );
  const [selectionType, setSelectionType] = useState<"version" | "label">(
    initialSelection?.type || "version",
  );

  // Notify parent component of selection changes
  useEffect(() => {
    onSelectionChange?.({
      type: selectionType,
      version: selectionType === "version" ? selectedVersion : null,
      label: selectionType === "label" ? selectedLabel : null,
    });
  }, [selectionType, selectedVersion, selectedLabel, onSelectionChange]);

  const handleVersionChange = (version: ListPromptVersion) => {
    setSelectedVersion(version);
  };

  const handleLabelSelect = (label: ReleaseLabel) => {
    setSelectedLabel(label);
  };

  const handleLabelRemove = () => {
    setSelectedLabel(null);
  };

  const handleGenericSelectionChange = (selection: {
    type: "first" | "second";
    first?: ListPromptVersion | null;
    second?: ReleaseLabel | null;
  }) => {
    const newSelectionType = selection.type === "first" ? "version" : "label";
    setSelectionType(newSelectionType);

    // Clear selections when switching tabs to avoid confusion
    if (newSelectionType === "version") {
      setSelectedLabel(null);
    } else {
      setSelectedVersion(null);
    }
  };

  const mappedInitialSelection = initialSelection
    ? {
        type: initialSelection.type === "version" ? ("first" as const) : ("second" as const),
        first: initialSelection.version,
        second: initialSelection.label,
      }
    : undefined;

  return (
    <GenericDualTabSelector<ListPromptVersion, ReleaseLabel>
      firstTabName="Prompt Version"
      secondTabName="Release Label"
      firstTabIcon={{
        outline: <DocumentTextIcon className="h-4 w-4" />,
        solid: <DocumentTextIconSolid className="h-4 w-4" />,
      }}
      secondTabIcon={{
        outline: <TagIcon className="h-4 w-4" />,
        solid: <TagIconSolid className="h-4 w-4" />,
      }}
      firstTabContent={
        <div className="m-2 space-y-2">
          <PromptVersionSelector
            promptId={promptId}
            onValueChange={handleVersionChange}
            selectedVersion={selectedVersion}
            showLabel={false}
            onRemove={() => {
              setSelectedVersion(null);
            }}
          />
        </div>
      }
      secondTabContent={
        <div className="m-2 space-y-2">
          <ReleaseLabelSelector
            selectedLabel={selectedLabel!}
            onLabelSelect={handleLabelSelect}
            onLabelRemove={handleLabelRemove}
            showLabel={false}
          />
        </div>
      }
      onSelectionChange={handleGenericSelectionChange}
      initialSelection={mappedInitialSelection}
    />
  );
};

export default PromptVersionLabelSelector;
export { GenericDualTabSelector };
